import classNames from "classnames";
import { Link } from "components/DesignSystem/Link/Link";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIcon } from "components/icons/Download";
import { Redirect } from "components/icons/Redirect";
import { FileObject } from "types/Models/fileObject";
import { toExternalUrl } from "util/toExternalUrl";

type SummaryDocument = Pick<
  FileObject,
  "uuid" | "name" | "file_type" | "is_previewable"
>;

type SummaryItem = {
  field_key: string;
  label: string;
  value: string | null;
  documents: SummaryDocument[];
  links: string[];
  is_full_width?: boolean;
};

type BooksHandoffSummaryData = {
  answered: number;
  total: number;
  items: SummaryItem[];
};

type SummaryFileProps = { document: SummaryDocument };

type SummaryItemViewProps = { item: SummaryItem };

type BooksHandoffSummaryProps = { data?: BooksHandoffSummaryData | null };

const SummaryFile = ({ document }: SummaryFileProps) => (
  <FilePreviewTag
    file={document}
    size="small"
    block
    className="t-max-w-64 sm:t-max-w-full !t-px-3 !t-py-2 t-bg-white"
  >
    <span className="t-text-text-30 t-flex">
      <DownloadIcon color="currentColor" />
    </span>
  </FilePreviewTag>
);

const SummaryItemView = ({
  item: { label, value, documents, links, is_full_width },
}: SummaryItemViewProps) => (
  <div
    className={classNames("t-flex t-flex-col t-gap-1.5 t-min-w-0", {
      "t-col-span-2 sm:t-col-span-1": is_full_width,
    })}
  >
    <span className="t-text-body-sm t-text-text-30">{label}</span>
    {value && (
      <span className="t-text-body-sm t-text-text-100 t-break-words t-whitespace-pre-wrap">
        {value}
      </span>
    )}
    {documents.map((document) => (
      <SummaryFile key={document.uuid} document={document} />
    ))}
    {links.map((link) => (
      <Link
        key={link}
        to={toExternalUrl(link)}
        target="_blank"
        className="t-inline-flex t-items-center t-gap-1.5 t-self-start t-text-body-sm !t-text-purple-50 hover:!t-text-purple-70"
      >
        link uploaded
        <span className="t-flex t-text-text-30">
          <Redirect size={14} />
        </span>
      </Link>
    ))}
  </div>
);

/** Read only view of the bookkeeping task's Books handoff checklist on the 1120 */
export const BooksHandoffSummary = ({ data }: BooksHandoffSummaryProps) => {
  if (!data) {
    return null;
  }

  return (
    <div>
      <div className="t-text-body-sm t-text-text-30 t-mb-4">
        {data.answered}/{data.total} details completed
      </div>
      <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-x-6 t-gap-y-5 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
        {data.items.map((item) => (
          <SummaryItemView key={item.field_key} item={item} />
        ))}
      </div>
    </div>
  );
};
